import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { useTimelineStore } from '@/components/Timeline/Timeline.store';
import { useApolloClient } from '@vue/apollo-composable';
import { DocumentNode } from 'graphql';
import { Ref, toValue } from 'vue';

export function useDeleteAllocation(
    variantsRef: Ref<TimelineVariant[]>,
    setVariantsRef: (value: TimelineVariant[]) => void,
) {
    const client = useApolloClient().client;
    const store = useTimelineStore();

    async function onDeleteAllocation(
        allocationId: string,
        deleteDocument: DocumentNode,
        onDone: (isSuccess: boolean) => void,
    ) {
        const result = await client.mutate({
            mutation: deleteDocument,
            variables: {
                allocationId,
            },
            update(cache, { data }) {
                const allocation = data?.delete?.allocation;

                if (!allocation) {
                    throw new Error(`Expected deleted allocation, got ${allocation}`);
                }
                cache.evict({
                    id: cache.identify(allocation),
                });
                cache.gc();
            },
        });
        if (result?.errors) {
            onDone(false);
        } else {
            const variantId = result?.data?.delete?.allocation?.variantId;
            const deletedAllocationId = result?.data?.delete?.allocation?.id;
            const locationId = result.data.delete.allocation.locationId;
            onDone(true);
            if (deletedAllocationId && variantId && locationId) {
                store.decrementLocationAllocationTotalCount(locationId);
                const newValue = toValue(variantsRef).map((v) => {
                    if (v.id === variantId) {
                        return {
                            ...v,
                            allocationsTotalCount: v.allocationsTotalCount - 1,
                            allocations: {
                                nodes: v.allocations.nodes.filter((a) => a.id !== deletedAllocationId),
                            },
                        };
                    }
                    return v;
                });
                setVariantsRef(newValue);
            }
        }
    }
    return {
        onDeleteAllocation,
    };
}
